import posthog, { PostHog } from 'posthog-js';

const screens = {
  '/admin/login': 'sign_in',
  '/admin': 'overview',
  '/admin/workforce': 'workforce',
  '/admin/tracking': 'tracking',
  '/admin/payroll': 'payroll',
  '/admin/devices': 'devices',
  '/admin/companies': 'companies',
  '/admin/settings': 'settings',
} as const;

type Screen = typeof screens[keyof typeof screens];
export type ProductAction = 'sign_in' | 'sign_out' | 'company_create' | 'device_provision' | 'device_revoke' | 'device_key_rotate' | 'tracking_refresh';
type Interaction = 'data_retry' | 'company_switch' | 'view_mode_switch' | 'device_filter' | 'company_form_opened' | 'device_form_opened';
type ProductEvent = '$pageview' | 'product_action_started' | 'product_action_completed' | 'product_action_failed' | 'product_interaction';
type Properties = { screen?: Screen; action?: ProductAction; interaction?: Interaction; duration_ms?: number; view_mode?: 'super_admin' | 'normal_user'; filter?: string };
const actions: ProductAction[] = ['sign_in', 'sign_out', 'company_create', 'device_provision', 'device_revoke', 'device_key_rotate', 'tracking_refresh'];
const interactions: Interaction[] = ['data_retry', 'company_switch', 'view_mode_switch', 'device_filter', 'company_form_opened', 'device_form_opened'];
const events: ProductEvent[] = ['$pageview', 'product_action_started', 'product_action_completed', 'product_action_failed', 'product_interaction'];
let client: PostHog | undefined;

export function productScreen(path: string): Screen | undefined {
  const normalized = path.replace(/\/+$/, '');
  return Object.prototype.hasOwnProperty.call(screens, normalized) ? screens[normalized as keyof typeof screens] : undefined;
}

function initialize(): PostHog | undefined {
  if (client) return client;
  const key = process.env.REACT_APP_POSTHOG_KEY;
  const host = process.env.REACT_APP_POSTHOG_HOST;
  if (process.env.NODE_ENV !== 'production' || !key || !host || !productScreen(window.location.pathname)) return;
  try {
    // A separate instance preserves the public site's homepage-only rules.
    client = posthog.init(key, {
      api_host: host,
      persistence: 'memory',
      person_profiles: 'never',
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      capture_exceptions: false,
      capture_dead_clicks: false,
      capture_performance: false,
      disable_session_recording: true,
      disable_surveys: true,
      disable_external_dependency_loading: true,
      advanced_disable_flags: true,
      ip: false,
      before_send: (event) => {
        if (!event || !events.includes(event.event as ProductEvent)) return null;
        const p = event.properties;
        const screen = Object.values(screens).includes(p.screen) ? p.screen : undefined;
        if (!screen) return null;
        // Rebuild every payload: never forward URLs, form contents, API bodies,
        // error messages, account/employee/company IDs, or DOM properties.
        event.properties = {
          token: p.token, distinct_id: p.distinct_id,
          $lib: p.$lib, $lib_version: p.$lib_version,
          $session_id: p.$session_id,
          $process_person_profile: false, $geoip_disable: true,
          app_area: 'product', analytics_version: 1, screen,
          action: actions.includes(p.action) ? p.action : undefined,
          interaction: interactions.includes(p.interaction) ? p.interaction : undefined,
          duration_ms: typeof p.duration_ms === 'number' && Number.isFinite(p.duration_ms) ? Math.max(0, Math.min(86400000, Math.round(p.duration_ms))) : undefined,
          view_mode: ['super_admin', 'normal_user'].includes(p.view_mode) ? p.view_mode : undefined,
          filter: ['all', 'PROVISIONED', 'ACTIVE', 'SUSPENDED', 'REVOKED'].includes(p.filter) ? p.filter : undefined,
        };
        return event;
      },
    }, 'waypoint_product');
  } catch {
    // Analytics is optional and must never prevent product use.
  }
  return client;
}

function capture(event: ProductEvent, properties: Properties, screen = productScreen(window.location.pathname)): void {
  if (!screen) return;
  try {
    initialize()?.capture(event, { ...properties, screen });
  } catch {
    // A blocked analytics service must not affect operations.
  }
}

export function trackProductPage(path: string, viewMode?: Properties['view_mode']): void {
  const screen = productScreen(path);
  if (screen) capture('$pageview', { view_mode: viewMode }, screen);
}

export function trackProductInteraction(interaction: Interaction, properties: Pick<Properties, 'view_mode' | 'filter'> = {}): void {
  capture('product_interaction', { ...properties, interaction });
}

export async function measureProductAction<T>(action: ProductAction, operation: () => Promise<T>): Promise<T> {
  const screen = productScreen(window.location.pathname);
  const started = Date.now();
  capture('product_action_started', { action }, screen);
  try {
    const result = await operation();
    capture('product_action_completed', { action, duration_ms: Date.now() - started }, screen);
    return result;
  } catch (error) {
    capture('product_action_failed', { action, duration_ms: Date.now() - started }, screen);
    throw error;
  }
}

export function resetProductAnalytics(): void {
  try { client?.reset(); } catch { /* Sign-out must work without analytics. */ }
}
